import { useState, useEffect } from "react";

import { KeyBasedObserver } from "../utils/key.observer.js";

/**
 * Custom hook that listens to any changes to a specific property of a model instance
 * by using the Key based observer passed to the metadata.
 *
 * @param {object} modelInstance - The model instance to observe.
 * @param {string} prop - The property to listen.
 * @returns {*} - The current value of the observed property.
 *
 * @example
 * new MetaDataStore(jsonData, new KeyBasedObserver())
 *
 * const MyComponent = () => {
 *   const propValue = useMetadataListener(actionModel, "Name");
 *
 *   Rest of the component logic
 * }
 */

export function useMetadataListener(modelInstance, prop) {
  const [targetValue, setTargetValue] = useState(modelInstance.getValue(prop));

  useEffect(function onMount() {
    // Check if keyObserver is added to metadata
    if (!(modelInstance.ds.observer instanceof KeyBasedObserver)) {
      console.warn(
        "Key observer is not added to metadata. This hook should only be used when keyObserver is added."
      );
      return;
    }

    const unsubscribe = modelInstance.ds.observer.observe(
      (target, property, value, receiver) => {
        if (property === prop) {
          if (value === undefined) {
            setTargetValue(null);
          } else if (
            ["boolean", "string", "number"].includes(typeof target[property])
          ) {
            setTargetValue(value);
          } else if (Array.isArray(target[property])) {
            setTargetValue([...target[property]]);
          }
        }
      },
      modelInstance.getValue("Id")
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return targetValue;
}
